import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Checkbox, TooltipHost } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";

import { getFileApiCall } from "../redux/apiCalls";
import { updateSetDocsForAttachToEmailData } from "../utils";
import { logError } from "../../../shared/services/errorLogging";
import { ROUTE } from "../../../shared/constants/routes";
import { TEXT } from "../../../shared/constants/texts";
import { SUPPORTED_PREVIEW_EXTENSIONS } from "../../../shared/constants/previewFileExtensions";
import ConfigService from "../../../shared/services/configService";
import SessionService from "../../../shared/services/sessionService";

const configService = new ConfigService();
const sessionService = new SessionService();

type AttachDocumentProps = {
  data: any;
  fromAgreement?: any;
};

const AgreementDocument: React.FC<AttachDocumentProps> = (props) => {
  const { data, fromAgreement = false } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.attachFromIciSlice);
  const { docsToAttach } = state;
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  const onCheckBoxClicked = (_e, checked, data, fromAgreement) => {
    if (checked) {
      const docs = [...docsToAttach];
      docs.push(data);
      updateSetDocsForAttachToEmailData(docs);
    } else {
      const docs = [...docsToAttach];
      let indexToDelete = 0;
      if (fromAgreement) {
        indexToDelete = docs.findIndex((doc) => doc.sysId === data.sysId);
      } else {
        indexToDelete = docs.findIndex((doc) => doc.SysId === data.SysId);
      }
      docs.splice(indexToDelete, 1);
      updateSetDocsForAttachToEmailData(docs);
    }
  };

  const onPreviewClicked = async (data, fromAgreement) => {
    let id = "",
      contractType = "",
      name = "",
      ext = "",
      fullFilePath = "",
      token = "";
    const isIdpEnabled = configService.getOauthFlag().toString();
    if (isIdpEnabled === "false") {
      token = sessionService.getAuthToken().replace(/['"]+/g, "");
    } else {
      token = sessionService.getIdpAccessToken();
    }
    if (fromAgreement) {
      id = data.sysId;
      contractType = data.entityName;
      name = data.name ? data.name : data.iCMAgreementCode;
      ext = getExtension(getFileName(data.filePath));
      fullFilePath = data.filePath.replace(/\.[^/.]+$/, ".Pdf");
    } else {
      id = data.SysId;
      contractType = data.ICMContractTypeName;
      name = data.Name ? data.Name : data.ICMAgreementCode;
      ext = getExtension(getFileName(data.FilePath));
      fullFilePath = data.FilePath.replace(/\.[^/.]+$/, ".Pdf");
    }
    if (SUPPORTED_PREVIEW_EXTENSIONS.includes(ext.toLowerCase())) {
      let previewUrl = window.location.href.replace(ROUTE.ATTACH_FROM_ICI, ROUTE.PREVIEW);
      previewUrl =
        previewUrl + "?token=" + encodeURIComponent(token) + "&fullFilePath=" + encodeURIComponent(fullFilePath);
      Office.context.ui.displayDialogAsync(
        previewUrl,
        { height: 90, width: 60, displayInIframe: true },
        (asyncResult) => {
          if (asyncResult && asyncResult.value) {
          } else {
            console.log("error previewing document - asyncResult failed:", asyncResult);
          }
        }
      );
    } else {
      const fileResponse = await getFileApiCall({
        ...apiCallParams,
        requestData: {
          agreementId: id,
          contractType: contractType,
          extension: ext,
        },
        retryFunctionCall: () => onPreviewClicked(data, fromAgreement),
      });
      const { response, error } = fileResponse;
      if (response) {
        let transformedResponse = response;
        if ("$value" in transformedResponse) {
          transformedResponse = transformedResponse["$value"];
        }
        downloadFile(name, ext, transformedResponse);
      }
      if (error) {
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|attach-from-ici|attach-document|AttachDocument - onPreviewClicked",
            callerApi: "Agreement/GetFile?fileType=&agreementId=&contractType=",
          },
        });
      }
    }
  };

  const downloadFile = (name, ext, base64) => {
    let downloadAnchor = document.createElement("a");
    downloadAnchor.href = "data:application/" + ext + ";base64," + base64;
    downloadAnchor.download = name + "." + ext;
    downloadAnchor.click();
  };

  const getFileName = (path) => {
    const file = path.split("\\");
    return file[file.length - 1];
  };

  const getExtension = (fileName) => {
    if (fileName) {
      let ext = fileName.split(".");
      return ext[ext.length - 1];
    }
  };

  const label = fromAgreement ? getFileName(data.filePath) : data.Name ? data.Name : data.ICMAgreementCode;
  let classNameForDocumentAttach = "document-attach-from-ici";
  if (!fromAgreement) {
    classNameForDocumentAttach = "document-attach-from-ici instance-document-attach-from-ici";
  }
  let ifDocIsSelected = false;
  if (fromAgreement) {
    const docs = [...docsToAttach].filter((doc) => doc.sysId === data.sysId);
    if (docs.length > 0) {
      ifDocIsSelected = true;
    }
  } else {
    const docs = [...docsToAttach].filter((doc) => doc.SysId === data.SysId);
    if (docs.length > 0) {
      ifDocIsSelected = true;
    }
  }

  return (
    <div className={classNameForDocumentAttach}>
      <div className="checkbox-icon">
        <Checkbox
          className="checkbox"
          onChange={(e, checked) => {
            onCheckBoxClicked(e, checked, data, fromAgreement);
          }}
          checked={ifDocIsSelected}
        />
      </div>
      <div className="label">
        <TooltipHost id="document-label-tooltip-attach-from-ici" content={label}>
          <p>{label}</p>
        </TooltipHost>
      </div>
      <div className="preview-icon" onClick={() => onPreviewClicked(data, fromAgreement)}>
        <Icon iconName="RedEye" />
      </div>
    </div>
  );
};

export default AgreementDocument;
